import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, ClassroomSettingsDto, ScheduleItemDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import { isoDate } from '../lib/localDate.js';
import { useClassroomDay } from '../hooks/useClassroomDay.js';
import { Pager, usePager } from './usePager.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import { PlannerSettings } from './PlannerSettings.js';
import styles from './settingsForm.module.css';

const SCHEDULE_PAGE_SIZE = 4;

// Weekdays only — Sat/Sun never apply to a school-week announcement.
const DAYS: ReadonlyArray<{ value: number; short: string; full: string }> = [
  { value: 1, short: 'Mon', full: 'Monday' },
  { value: 2, short: 'Tue', full: 'Tuesday' },
  { value: 3, short: 'Wed', full: 'Wednesday' },
  { value: 4, short: 'Thu', full: 'Thursday' },
  { value: 5, short: 'Fri', full: 'Friday' },
];

const DAY_BY_VALUE: Record<number, { short: string; full: string }> = Object.fromEntries(
  DAYS.map((d) => [d.value, { short: d.short, full: d.full }]),
);

function dayLabel(days: number[]): string {
  // Weekday-only: 5 selected means "every school day".
  const weekdays = days.filter((d) => d >= 1 && d <= 5);
  if (weekdays.length === 5) return 'Every day';
  if (weekdays.length === 0) return '—';
  const sorted = [...weekdays].sort((a, b) => a - b);
  return sorted.map((d) => DAY_BY_VALUE[d]?.short ?? '?').join(', ');
}

export function HomePageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;

  const settingsQuery = useQuery<ClassroomSettingsDto>({
    queryKey: ['classroom-settings', classroomId],
    queryFn: () => api.home.getSettings(classroomId),
  });

  const scheduleQuery = useQuery<ScheduleItemDto[]>({
    queryKey: ['classroom-schedule', classroomId],
    queryFn: () => api.home.listSchedule(classroomId),
  });

  const resetAll = useMutation({
    mutationFn: async () => {
      await api.home.patchSettings(classroomId, { greeting: '', message: '' });
      const items = await api.home.listSchedule(classroomId);
      await Promise.all(items.map((it) => api.home.removeScheduleItem(classroomId, it.id)));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom-settings', classroomId] });
      void queryClient.invalidateQueries({ queryKey: ['classroom-schedule', classroomId] });
    },
  });

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Home</h2>
          <p className={styles.description}>
            The greeting + message-of-the-day card and the announcement strip the kids see when
            class starts.
          </p>
        </div>
        <ResetSettingsButton
          pageLabel="Home"
          onReset={() => resetAll.mutate()}
          disabled={resetAll.isPending}
        />
      </header>

      <div className={styles.form}>
        <GreetingMessageFields
          classroomId={classroomId}
          settings={settingsQuery.data}
          isLoading={settingsQuery.isLoading}
          onSaved={(next) => {
            queryClient.setQueryData<ClassroomSettingsDto>(
              ['classroom-settings', classroomId],
              next,
            );
          }}
        />

        <ScheduleSection
          classroomId={classroomId}
          items={scheduleQuery.data ?? []}
          isLoading={scheduleQuery.isLoading}
        />

        {/* 4–8 classrooms also plan a weekly To-Do + Turn-In board here. */}
        {settingsQuery.data?.homeVariant === 'g48' ? (
          <PlannerSettings classroomId={classroomId} />
        ) : null}
      </div>
    </div>
  );
}

interface GreetingMessageFieldsProps {
  classroomId: string;
  settings: ClassroomSettingsDto | undefined;
  isLoading: boolean;
  onSaved: (next: ClassroomSettingsDto) => void;
}

function GreetingMessageFields({
  classroomId,
  settings,
  isLoading,
  onSaved,
}: GreetingMessageFieldsProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  // Greeting is persistent (classroom_settings). The message is per-day:
  // edits save to today's classroom_day row and the field shows today's
  // effective (carried-forward) value — so the teacher sees the current
  // message, never blank, and changing it records that day's history.
  const today = isoDate();
  const day = useClassroomDay(classroomId);
  const [greeting, setGreeting] = useState(settings?.greeting ?? '');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Hydrate local state once each query resolves; re-sync when the classroom
  // changes underneath us.
  useEffect(() => {
    if (settings) setGreeting(settings.greeting);
  }, [settings, classroomId]);
  useEffect(() => {
    if (day.data) setMessage(day.data.message);
  }, [day.data, classroomId]);

  const saveGreeting = useMutation({
    mutationFn: (next: string) => api.home.patchSettings(classroomId, { greeting: next }),
    onMutate: () => setStatus('saving'),
    onSuccess: (next) => {
      onSaved(next);
      setStatus('saved');
    },
    onError: () => setStatus('error'),
  });

  const saveMessage = useMutation({
    mutationFn: (next: string) => api.dayHistory.putDay(classroomId, today, { message: next }),
    onMutate: () => setStatus('saving'),
    onSuccess: (nextDay) => {
      queryClient.setQueryData(['classroom-day', classroomId, today], nextDay);
      setStatus('saved');
    },
    onError: () => setStatus('error'),
  });

  const onGreetingBlur = () => {
    if (settings && greeting !== settings.greeting) saveGreeting.mutate(greeting);
  };
  const onMessageBlur = () => {
    const current = day.data?.message ?? '';
    if (day.data && message !== current) saveMessage.mutate(message);
  };

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor="home-greeting">
          Greeting
        </label>
        <input
          id="home-greeting"
          className={styles.input}
          type="text"
          value={greeting}
          placeholder="Good morning!"
          maxLength={120}
          disabled={isLoading}
          onChange={(e) => setGreeting(e.target.value)}
          onBlur={onGreetingBlur}
        />
        <span className={styles.hint}>
          Shown above the message card. Leave blank to hide the greeting line.
        </span>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="home-message">
          Teacher&apos;s message
        </label>
        <textarea
          id="home-message"
          className={styles.textarea}
          value={message}
          placeholder="e.g. Don't forget your library books today!"
          maxLength={500}
          disabled={day.isLoading}
          rows={2}
          onChange={(e) => setMessage(e.target.value)}
          onBlur={onMessageBlur}
        />
      </div>

      <SaveStatus status={status} />
    </>
  );
}

interface ScheduleSectionProps {
  classroomId: string;
  items: ScheduleItemDto[];
  isLoading: boolean;
}

function ScheduleSection({ classroomId, items, isLoading }: ScheduleSectionProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  // Default: nothing selected. The Add button stays disabled until the
  // teacher both names the announcement and picks at least one day.
  const [days, setDays] = useState<Set<number>>(() => new Set());

  const addItem = useMutation({
    mutationFn: (body: { text: string; days: number[] }) =>
      api.home.createScheduleItem(classroomId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom-schedule', classroomId] });
      setText('');
    },
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.home.removeScheduleItem(classroomId, itemId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom-schedule', classroomId] });
    },
  });

  const toggleDay = (d: number) => {
    setDays((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || days.size === 0) return;
    addItem.mutate({ text: trimmed, days: [...days].sort((a, b) => a - b) });
  };

  const sortedItems = [...items].sort((a, b) => a.position - b.position);
  const pager = usePager(sortedItems, SCHEDULE_PAGE_SIZE);

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Scheduled announcements</h3>
      <p className={styles.hint}>
        Recurring callouts (Library, Computer, Chapel…). Shows on the Home strip on the chosen days.
      </p>

      {isLoading ? (
        <p className={styles.empty}>Loading…</p>
      ) : sortedItems.length === 0 ? (
        <p className={styles.empty}>No announcements yet — add one below.</p>
      ) : (
        <>
          <ul className={styles.list}>
            {pager.visible.map((item) => (
              <li key={item.id} className={styles.listItem}>
                <div className={styles.listItemMain}>
                  <span className={styles.listItemTitle}>{item.text}</span>
                  <span className={styles.listItemMeta}>{dayLabel(item.days)}</span>
                </div>
                <button
                  type="button"
                  className={styles.buttonGhost}
                  disabled={removeItem.isPending}
                  onClick={() => removeItem.mutate(item.id)}
                  aria-label={`Remove ${item.text}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
          <Pager
            page={pager.page}
            totalPages={pager.totalPages}
            onNext={pager.next}
            onPrev={pager.prev}
            itemLabel="announcements"
          />
        </>
      )}

      <form className={styles.formRow} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="schedule-text">
            New announcement
          </label>
          <input
            id="schedule-text"
            className={styles.input}
            type="text"
            value={text}
            placeholder="e.g. Library"
            maxLength={80}
            onChange={(e) => setText(e.target.value)}
          />
          <div className={styles.dayPicker} role="group" aria-label="Days">
            {DAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                className={`${styles.dayChip} ${days.has(d.value) ? styles.dayChipActive : ''}`}
                aria-pressed={days.has(d.value)}
                onClick={() => toggleDay(d.value)}
              >
                {d.short}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          className={`${styles.button} ${styles.buttonPrimary}`}
          disabled={!text.trim() || days.size === 0 || addItem.isPending}
        >
          {addItem.isPending ? 'Adding…' : 'Add'}
        </button>
      </form>
    </div>
  );
}

function SaveStatus({ status }: { status: 'idle' | 'saving' | 'saved' | 'error' }) {
  if (status === 'idle') return null;
  if (status === 'saving') return <span className={styles.statusLine}>Saving…</span>;
  if (status === 'saved') return <span className={styles.statusLine}>Saved</span>;
  return <span className={`${styles.statusLine} ${styles.statusError}`}>Save failed</span>;
}
